import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Disclosure } from "../../../components/Disclosure";
import { type InfoOF, type InfoFase } from "../../../ts/api/API";
import { ComunicacionAjax } from "../../../ts/api/comunicacion";
import { Constantes } from "../../../ts/constantes";
import { Breadcrumb } from "../../../components/Breadcrumb";
import "./GestionOF.scss";
import { getSelectionCell, TypedTable } from "../../../components/TypedTable";
import { createColumnHelper, type OnChangeFn } from "@tanstack/react-table";
import { ModalIniciarFase } from "./components/IniciarFase";
import { ModalVerImputacionesFase } from "./components/VerImputacionesFase";
import { ModalCerrarFase } from "./components/CerrarFase";
import { useDisclosureData } from "../../../hooks/UseDisclosure";
import { ModalImputarFase } from "./components/ImputarFase";
import { Loading, QueryError } from "../../../components/Loading/Loading";

export function GestionOFsPage() {
	const queryClient = useQueryClient();
	const [filtroMaquina, setFiltroMaquina] = useState<string | undefined>(undefined);
	const [ofFocused, setOFFocused] = useState<InfoOF | undefined>(undefined);
	const [selectedOFs, setSelectedOFs] = useState<Record<string, boolean>>({});



	return (
		<div id="content" className="content" >
			<title>Almacen | Gestión de OFs </title>
			<Breadcrumb
				sectionTitle="Gestión de OFs"
				links={[
					{ label: "Gestión de OFs", active: true }
				]}
			/>

			<Disclosure style={{ marginBottom: 20 }}>
				<h4 className="panel-title panel-heading senalar">
					Filtro
				</h4>
				<Filtros filtroMaquina={filtroMaquina} setFiltroMaquina={setFiltroMaquina} />
			</Disclosure>

			<TablaOrdenesGestor
				filtroMaquina={filtroMaquina}
				ofFocused={ofFocused}
				setOFFocused={setOFFocused}
				selectedOFs={selectedOFs}
				setSelectedOFs={setSelectedOFs}
			/>

			{ofFocused ? (
				<div className="table-responsive">
					<TablaFasesOF of={ofFocused} />
				</div>
			) : null}
		</div>
	);
}

interface FiltrosProps {
	readonly filtroMaquina?: string;
	readonly setFiltroMaquina: (maquina?: string) => void;
}
function Filtros(props: FiltrosProps) {
	const onChangeMaquina = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.target.value;
		if (value === "") {
			props.setFiltroMaquina(undefined);
		} else {
			props.setFiltroMaquina(value);
		}
	};

	const { data, error, isPending } = useMaquinasYCarga();

	if (error) return <QueryError error={error} fallback="Error al cargar las maquinas" />;
	if (isPending) return <Loading />;

	return (
		<div style={{ display: "flex", alignItems: "flex-end", gap: 15, padding: 30 }} role="tabpanel">
			<label style={{ fontSize: "13pt" }}>Puestos: </label>
			<select className="form-control limpiar" onChange={onChangeMaquina} value={props.filtroMaquina ?? ""}>
				<option value="">Elige</option>
				{data.map((maquina) => (
					<option value={maquina.idPuesto} key={maquina.idPuesto}>
						{maquina.descripcion}
						-
						Carga de trabajo: {maquina.cantidadHoras}h
					</option>
				))}
			</select>
		</div>
	);
}

interface TablaOrdenesGestorProps {
	readonly filtroMaquina: string | undefined;
	readonly ofFocused?: InfoOF;
	readonly setOFFocused: (of: InfoOF | undefined) => void;
	readonly selectedOFs: Record<string, boolean>;
	readonly setSelectedOFs: OnChangeFn<Record<string, boolean>>;
}
function TablaOrdenesGestor(props: TablaOrdenesGestorProps) {
	const { setSelectedOFs } = props;
	const idSituacionToString = (orden: InfoOF) => {
		let textoSituacion = "";
		// const porcentaje = (orden.horasImputadas / orden.horasCalculadas) * 100;
		// const formatter = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 1 });	//Yo encantado usaria style:percent, pero se niega a formatear en "narrow"
		if (orden.cantAcabada >= orden.cantLanzada)
			textoSituacion = "Terminado";
		else
			textoSituacion = "En curso";

		return textoSituacion;
	};
	const onClickRow = (e: React.MouseEvent, data: InfoOF) => {
		const target = e.target as HTMLElement;
		if (e.ctrlKey) { // Toggle checkbox on ctrl+click
			const checkbox = target.closest("tr")?.querySelector("input[type='checkbox']") as HTMLInputElement | null;
			if (!checkbox) throw new Error("la fila no tiene checkbox");

			checkbox.checked = !checkbox.checked;
			return;
		}
		if (target.closest(".noClick")) return; //Si he hecho click en el checkbox, no entro a detalles

		props.setOFFocused(data);
	};

	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<InfoOF>();
		return [
			getSelectionCell(columnHelper, { meta: { className: "dt-center noClick" } }),

			columnHelper.accessor("idOF", { header: "Número de OF" }),
			// columnHelper.accessor("idConjunto", { header: "Conjunto" }),
			columnHelper.accessor("descripcion", { header: "Descripción" }),
			columnHelper.display({ id: "Situación", header: "Situación", cell: info => idSituacionToString(info.row.original) }),
			columnHelper.accessor("cantLanzada", { header: "Cantidad" }),
			// { title: "Fecha inicio previsto", render: (ts) => displayDate(ts.horasCalculadas) },
		];
	}, []);

	const { data, error, isPending } = useOFsEnCurso(props.filtroMaquina);


	// Reset selected rows when data changes
	useEffect(() => {
		setSelectedOFs({});
	}, [setSelectedOFs, data]);

	if (error) return <QueryError error={error} fallback="Error al cargar las ordenes de fabricacion" />;
	if (isPending) return <Loading />;

	return (
		<TypedTable
			className="dataTable display clicable"
			data={data}
			// "scrollY": "400px",
			// "scrollCollapse": true,
			columns={columns}
			onRowClick={onClickRow}
			rowClassName={row => props.ofFocused?.idOF === row.idOF ? "clicked" : ""}

			pagination
			globalSearch

			selection={props.selectedOFs}
			getRowId={row => row.idOF.toString()}
			setSelection={setSelectedOFs}
		/>
	);
}

function TablaFasesOF(props: { readonly of: InfoOF; }) {
	const { open: openIniciar, ...discloseIniciar } = useDisclosureData<number>(-1);
	const { open: openImputar, ...discloseImputar } = useDisclosureData<number>(-1);
	const { open: openCerrar, ...discloseCerrar } = useDisclosureData<number>(-1);
	const { open: openImputaciones, ...discloseImputaciones } = useDisclosureData<number>(-1);


	const { data, error, isPending } = useFasesOF(props.of.idOF);
	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<InfoFase>();

		const displayDate = (dateTs: number | null) => {
			if (dateTs == null) return "-";
			const date = new Date(dateTs * 1000);
			const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };

			return date.toLocaleString("es-ES", options);
		};
		const getAccionesFase = (row: InfoFase) => {
			const onIniciarFase = (row: InfoFase) => openIniciar(row.idFase);
			const onImputarFase = (row: InfoFase) => openImputar(row.idFase);
			const onCerrarFase = (row: InfoFase) => openCerrar(row.idFase);
			const onVerImputaciones = (row: InfoFase) => openImputaciones(row.idFase);


			if (row.idPuesto == Constantes.PUESTO_OPE) {
				if (row.timestampIni == null)
					return (
						<div className="btnsFase">
							<button className="btnPrimary" onClick={() => onIniciarFase(row)} type="button">Iniciar</button>
						</div>
					);

				if (row.cantLanzada > row.cantAcabada && row.timestampFin == null)
					return (
						<div className="btnsFase">
							<button disabled type="button">Entrega de la OPE sin completar</button>
						</div>
					);

				if (row.timestampFin == null)
					return (
						<div className="btnsFase">
							<button className="btnSecondary" onClick={() => onCerrarFase(row)} type="button"> Cerrar </button>
						</div>
					);

				// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Stilliscic
				if (row.timestampFin != null)
					return (
						<div className="btnsFase">
							{/* <button className="btnCerrarOF"> Terminada </button> */}
						</div>
					);
			} else {
				if (row.situacion === 0)
					return (
						<div className="btnsFase">
							<button className="btnPrimary" onClick={() => onIniciarFase(row)} type="button">Iniciar</button>
							<button className="btnPrimary" onClick={() => onImputarFase(row)} type="button"> Imputar </button>
						</div>
					);

				if (row.cantLanzada > row.cantAcabada && row.situacion === 1)
					return (
						<div className="btnsFase">
							<button className="btnPrimary" onClick={() => onImputarFase(row)} type="button"> Imputar </button>
							<button className="btnSecondary" onClick={() => onVerImputaciones(row)} type="button"> Ver Imputaciones </button>
							<button className="btnSecondary" onClick={() => onCerrarFase(row)} type="button"> Cerrar </button>
						</div>
					);

				if (row.cantLanzada <= row.cantAcabada && row.situacion === 1)
					return (
						<div className="btnsFase">
							<button className="btnSecondary" onClick={() => onVerImputaciones(row)} type="button">Ver Imputaciones</button>
							<button className="btnSecondary" onClick={() => onCerrarFase(row)} type="button">Cerrar</button>
						</div>
					);

				if (row.situacion === 2)
					return (
						<div className="btnsFase">
							<button className="btnSecondary" onClick={() => onVerImputaciones(row)} type="button">Ver Imputaciones</button>
						</div>
					);
			}
		};
		const renderDesc = (original: InfoFase, desc: string) => {
			if (original.idPuesto == Constantes.PUESTO_OPE) {
				return <span style={{ color: "red", fontWeight: "bold" }}>OPE - {desc}</span>;
			} else {
				return desc;
			}
		};


		return [
			columnHelper.accessor("idFase", { header: "Fase", meta: { className: "dt-center", hidden: !Constantes.existenFases } }),
			columnHelper.accessor("descripcion", { header: "Descripción", meta: { className: "dt-left" }, cell: ({ row, getValue }) => renderDesc(row.original, getValue()) }),

			columnHelper.accessor("cantLanzada", { header: "Cant. Lanzada", meta: { className: "dt-center" } }),
			columnHelper.accessor("cantAcabada", { header: "Cant. Acabada", meta: { className: "dt-center" } }),

			columnHelper.accessor("timestampIni", { header: "Fecha inicio", meta: { className: "dt-center" }, cell: info => displayDate(info.getValue()) }),
			columnHelper.accessor("timestampFin", { header: "Fecha fin", meta: { className: "dt-center" }, cell: info => displayDate(info.getValue()) }),

			columnHelper.display({ id: "Acciones", header: "Acciones", meta: { className: "dt-center" }, cell: info => getAccionesFase(info.row.original) }),
		];
	}, [openCerrar, openImputaciones, openImputar, openIniciar]);

	if (error) return <QueryError error={error} fallback="Error al cargar las fases" />;
	if (isPending) return <Loading />;

	return <>
		<TypedTable
			className="dataTable display"
			data={data}
			columns={columns}
			pagination
			globalSearch
		// "scrollY": "400px",
		// "scrollCollapse": true,
		/>

		<ModalIniciarFase
			idOF={props.of.idOF}
			idFase={discloseIniciar.data}
			show={discloseIniciar.isOpen}
			close={discloseIniciar.close}
		/>
		<ModalImputarFase
			idOF={props.of.idOF}
			idFase={discloseImputar.data}
			show={discloseImputar.isOpen}
			close={discloseImputar.close} />
		<ModalCerrarFase
			idOF={props.of.idOF}
			idFase={discloseCerrar.data}
			show={discloseCerrar.isOpen}
			close={discloseCerrar.close} />
		<ModalVerImputacionesFase
			idOF={props.of.idOF}
			descConjunto={props.of.descripcion}
			idFase={discloseImputaciones.data}
			show={discloseImputaciones.isOpen}
			close={discloseImputaciones.close} />
	</>;
}




function useOFsEnCurso(idMaquina?: string) {
	return useQuery<InfoOF[]>({
		queryKey: ["getOFsEnCurso", idMaquina ?? "todas"],
		queryFn: () => ComunicacionAjax.sendAjaxRequest<InfoOF[]>("GET", "/api/v1/getOFsEnCurso", { idMaquina: idMaquina }),
	});

}
function useFasesOF(idOF: number) {
	return useQuery<InfoFase[]>({
		queryKey: ["getFasesOF", idOF],
		queryFn: () => ComunicacionAjax.sendAjaxRequest<InfoFase[]>("GET", "/api/v1/getFasesOF", { idOF: idOF }),
	});
}
function useMaquinasYCarga() {
	return useQuery<MaquinaConCarga[]>({
		queryKey: ["getMaquinasConCarga"],
		queryFn: () => ComunicacionAjax.sendAjaxRequest<MaquinaConCarga[]>("GET", "/api/v1/getMaquinasConCarga"),
	});
}



interface MaquinaConCarga {
	idPuesto: string;
	descripcion: string;
	cantidadHoras: number;
}